<script setup>
import {computed, onMounted, ref} from "vue";
import {useMutation} from "@vue/apollo-composable";
import Modal from "@/components/modal/Modal.vue";
import AllPersonsStrip from "@/components/hpo/dashboard/patientListItems/AllPersonsStrip.vue";
import {InfolinePatientDetailMutation} from "@/graphql/mutations/hpo/infoline/patient_detail.graphql";

const props = defineProps({
  patientId: {
    type: String,
    required: true,
  },
  hpo: {
    type: Object,
    required: true,
  },
  member: {
    type: Object,
    required: true,
  },
  countries: {
    type: Array,
    required: true,
  },
});

const emit = defineEmits(['close']);

function close() {
  emit('close');
}

// A mutation (not a query) so an access log can be added server-side later without changing
// this contract. Fired once on open — the modal is only mounted (v-if) while a patient is
// selected, so opening == fetching and closing unmounts.
const {mutate, error} = useMutation(InfolinePatientDetailMutation);
const patient = ref(null);
const loaded = ref(false);

onMounted(async () => {
  try {
    const response = await mutate({hpoId: props.hpo.id, patientId: props.patientId});
    patient.value = response?.data?.infolinePatientDetailMutation?.patient ?? null;
  } catch (e) {
    // useMutation's `error` ref captures the failure; patient stays null -> "not found" alert.
  } finally {
    loaded.value = true;
  }
});

// Same code -> name mapping AllPersonsList uses, so AllPersonsStrip gets a resolved name.
const countryChoices = computed(() =>
  props.countries.reduce((obj, item) => Object.assign(obj, {[item.code]: item.name}), {})
);
const countryName = computed(() => {
  const code = patient.value?.country;
  return code ? (countryChoices.value[code] ?? '–') : '–';
});
</script>

<template>
  <Modal :title="pgettext('title', 'Patient Detail')" @close="close" :max-width="1200">
    <div v-if="!loaded" class="d-flex justify-center pa-8">
      <v-progress-circular indeterminate/>
    </div>
    <v-alert v-else-if="error || !patient"
             type="error"
             :title="pgettext('message', 'Patient not found')"/>
    <AllPersonsStrip v-else
                     :patient="patient"
                     :member="member"
                     :readOnly="true"
                     :countryName="countryName"/>
  </Modal>
</template>

<style scoped>
</style>
